import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NestedPathHelper } from '../helpers/nested-path.helper';

// Keeps a stack of visited routes so the sidebar Back button can retrace the real
// path the user took (calendar -> card -> course -> back -> card -> back -> calendar)
// instead of only remembering a single previous URL (which ping-pongs). Entries are
// deduped per entity/context, so switching tabs within one entity is not a back step.
@Injectable()
export class NavigationHistoryService {
  private static readonly MAX_ENTRIES = 50;
  private stack: string[] = [];
  private editorBackTargets = new Map<string, string>();

  constructor(private router: Router) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.record(event.urlAfterRedirects || event.url);
      }
    });
  }

  private record(url: string): void {
    const key = NestedPathHelper.contextKey(url);
    const topKey = this.stack.length ? NestedPathHelper.contextKey(this.stack[this.stack.length - 1]) : null;
    const prevKey = this.stack.length >= 2 ? NestedPathHelper.contextKey(this.stack[this.stack.length - 2]) : null;

    // Same context as the current top (e.g. a tab switch within one entity): update
    // the stored URL in place, don't grow the stack.
    if (topKey !== null && key === topKey) {
      this.stack[this.stack.length - 1] = url;
      return;
    }

    // Navigated back to the previous context: pop the current top.
    if (prevKey !== null && key === prevKey) {
      this.stack.pop();
      this.stack[this.stack.length - 1] = url;
      return;
    }

    this.stack.push(url);
    if (this.stack.length > NavigationHistoryService.MAX_ENTRIES) {
      this.stack.shift();
    }
  }

  getBackTarget(): string | null {
    if (this.stack.length === 0) {
      return null;
    }

    const currentIndex = this.stack.length - 1;
    const currentKey = NestedPathHelper.contextKey(this.stack[currentIndex]);
    for (let i = currentIndex - 1; i >= 0; i--) {
      const entryKey = NestedPathHelper.contextKey(this.stack[i]);
      const isCurrentPage = entryKey === currentKey;
      const isChildOfCurrentPage = entryKey.startsWith(currentKey + '/');
      if (!isCurrentPage && !isChildOfCurrentPage) {
        return this.stack[i];
      }
    }
    return null;
  }

  /**
   * Back target for an editor sidebar: the page the editor was opened from, or null when it was
   * opened directly (link, refresh). Call from the editor's ngOnInit.
   *
   * Coming back up from one of the entity's own children (child Back, parent breadcrumb tab) keeps
   * the target the editor had before — pointing Back at the child would loop parent <-> child.
   */
  getEditorBackTarget(editorUrl: string): string | null {
    const entityPath = NestedPathHelper.contextKey(editorUrl);
    const previousUrl = this.getPreviousUrl();

    if (!previousUrl) {
      this.editorBackTargets.delete(entityPath);
      return null;
    }

    const cameFromChild = NestedPathHelper.contextKey(previousUrl).startsWith(entityPath + '/');
    if (cameFromChild) {
      return this.editorBackTargets.get(entityPath) ?? null;
    }

    this.rememberEditorBackTarget(entityPath, previousUrl);
    return previousUrl;
  }

  private rememberEditorBackTarget(entityPath: string, url: string): void {
    this.editorBackTargets.delete(entityPath);
    this.editorBackTargets.set(entityPath, url);

    if (this.editorBackTargets.size > NavigationHistoryService.MAX_ENTRIES) {
      const oldestKey = this.editorBackTargets.keys().next().value;
      this.editorBackTargets.delete(oldestKey);
    }
  }

  private getPreviousUrl(): string | null {
    const navigation = this.router.getCurrentNavigation() ?? this.router.lastSuccessfulNavigation;
    const previousNavigation = navigation?.previousNavigation;
    if (!previousNavigation) {
      return null;
    }
    return this.router.serializeUrl(previousNavigation.finalUrl ?? previousNavigation.initialUrl);
  }
}
